import { Store } from '../../infra/store.service'
import { Company } from '../companies/company'
import { DevicesRepository } from '../devices/devices.repository'
import { DevicesService } from '../devices/devices.service'
import { Token } from '../token/token'
import { User } from '../users/user'
import type { AuthRepository } from './auth.repository'
import type { CreatePasswordDto } from './dto/create-password.dto'
import type { LoginPasswordDTO } from './dto/login-password.dto'
import type { LoginSMSDTO } from './dto/login-sms.dto'
import type { RequestEMailDTO } from './dto/request-email.dto'
import type { requestSMSDTO } from './dto/request-sms.dto'
import type { UpdatePasswordDto } from './dto/update-password.dto'

export class AuthService {
    private store: Store
    constructor(private rep: AuthRepository) {
        this.store = new Store()
    }

    async setTokenToAutoRenew() {
        // const secondsIssued = this.dateDiffSeconds(new Date(+this.store.token.iat * 1000), new Date())
        // const secondsLifetime = this.dateDiffSeconds(
        //     new Date(+this.store.token.iat * 1000),
        //     new Date(+this.store.token.exp * 1000),
        // )
        const secondsToExpires = this.dateDiffSeconds(new Date(), new Date(+this.store.token.exp * 1000))
        //console.log('Token atual expira em: ' + (secondsToExpires / 60).toFixed() + ' minutos')
        let schedulerTime = 60 * 1000 * 15 //renovação a cada 15 minutos
        //console.log('Renovação do token ocorrerá em ' + (schedulerTime / 1000 / 60).toFixed() + ' minutos')
        setTimeout(async () => {
            try {
                await this.refreshToken(this.store.token.token, true)
            } catch (error) {
                console.log('refresh_token_error (1)', error)
            }
        }, schedulerTime)
    }

    async logout(logoutAllDispositives: boolean) {
        console.log('auth logging out...')
        if (logoutAllDispositives) {
            await this.invalidateTokens()
        }
        try {
            await window.api.users.devices.refreshToken()
        } catch (error) {
            console.warn('Error resetting token', error)
        }
        const store = new Store()
        store.token = new Token()
        store.company = new Company()
        store.user = new User()
        store.alert = false
    }

    isTokenNearToExpires() {
        const diff = this.dateDiffSeconds(new Date(), new Date(+this.store.token.exp * 1000))
        if (diff < 0) return false
        return diff < 60 * 10
    }

    isTokenExpired() {
        return this.dateDiffSeconds(new Date(), new Date(+this.store.token.exp * 1000)) < 0
    }

    isValid() {
        if (!this.store.token) return false
        const tokenDate = new Date(this.store.token.exp * 1000)
        const currentDate = new Date()
        const result = Math.round((+tokenDate - +currentDate) / 1000) > 0
        return result
    }

    isCompanySelected() {
        return !!this.store.token.companyId
    }

    private dateDiffSeconds(date1: Date, date2: Date) {
        return Math.round((+date2 - +date1) / 1000)
    }

    // ==========

    async invalidateTokens() {
        return await this.rep.invalidateTokens()
    }

    async refreshToken(token: string, autoRenew: boolean = false) {
        if (!token) return null
        if (autoRenew) await this.setTokenToAutoRenew()
        try {
            const newToken = await this.rep.refreshToken(token)
            // caso o token esteja vencido/prestes a vencer, então aguarda seu vencimento para forçar o relogin
            const nearToExpires = new Date(newToken.exp * 1000).getTime() - new Date().getTime() < 10000
            //console.log('nearToExpires', nearToExpires)
            if (nearToExpires) await new Promise((resolve) => setTimeout(resolve, 5000))
            this.store.token = newToken
            return newToken
        } catch (error) {
            // a rotina abaixo não é mais disparada. NOVA ABORDAGEM: não irá disparar o erro, mas enviará um token prestes a vencer.
            // NO ENTANTO a rotina abaixo deve continuar, pois se a conexão do usuário for lenta o token chegará vencido
            console.log('refresh_token_error  (2)', error)
            if (error?.includes('New login required') || error?.includes('Token not allowed') || error?.includes('Unauthorized')) {
                await this.logout(false)
                location.reload()
            }
        }
    }

    async requestLoginByToken(userId: string) {
        return await this.rep.requestLoginByToken(userId)
    }

    async loginByPassword(loginPasswordDto: LoginPasswordDTO) {
        const token = await this.rep.loginByPassword(loginPasswordDto)
        this.store.token = token
        this.store.user = await window.api.users.users.getOne(token.userId)
        console.log('login by pass', window.store.user)
        return token
    }

    async loginBySMS(loginSmsDto: LoginSMSDTO) {
        const token = await this.rep.loginBySMS(loginSmsDto)
        this.store.token = token
        this.store.user = await window.api.users.users.getOne(token.userId)
        console.log('login by sms', window.store.user)
        return token
    }

    async requestSignupBySMS(requestSMS: requestSMSDTO) {
        // check if is phone. evaluate request response, and then return response
        const result = await this.rep.requestSignupBySMS(requestSMS)
    }

    async requestLoginBySMS(requestSMS: requestSMSDTO) {
        // check if is phone. evaluate request response, and then return response
        const result = await this.rep.requestLoginBySMS(requestSMS)
    }

    async requestLoginByEmail(requestEmail: RequestEMailDTO) {
        const url = window.location.href + '?renew=1&token={token}'
        const result = await this.rep.requestLoginByEmail(requestEmail.email, url)
    }

    async updatePassword(data: UpdatePasswordDto) {
        await this.rep.updatePassword(data)
    }

    async createPassword(createPasswordDto: CreatePasswordDto) {
        if (createPasswordDto.password.length < 6) {
            throw 'A senha deve conter no mínimo 6 caracters.'
        }
        try {
            await this.rep.createPassword(createPasswordDto)
        } catch (error) {
            throw error
        }
    }

    async getCompanyToken(companyId, token: string) {
        const newToken = await this.rep.getCompanyToken(companyId, token)
        const deviceS = new DevicesService(new DevicesRepository())
        this.store.token = newToken
        deviceS.upsertDevice() // não utilizar await, para não bloquear o retorno
        return newToken
    }
}
